import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import Swal from 'sweetalert2';

const companySchema = z.object({
  name: z.string().min(1, 'validation.required'),
  email: z.string().email('validation.email').min(1, 'validation.required'),
  address: z.string().min(1, 'validation.required'),
  postalCode: z.string().min(1, 'validation.required'),
  city: z.string().min(1, 'validation.required'),
  iban: z.string().min(1, 'validation.required'),
  bic: z.string().min(1, 'validation.required'),
  bankName: z.string().min(1, 'validation.required'),
  taxNumber: z.string().optional(),
  // File fields are handled separately, not validated by Zod
  letterheadPath: z.any().optional(),
  stampPath: z.any().optional(),
  signaturePath: z.any().optional(),
});

const Companies = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Fetch companies
  const { data: companies, isLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    },
  });

  // Create company mutation
  const createMutation = useMutation({
    mutationFn: async (formData) => {
      console.log('Creating company with FormData');
      const response = await api.post('/companies', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Company created successfully:', response.data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setShowForm(false);
      setEditingCompany(null);
      reset({
        name: '',
        email: '',
        address: '',
        postalCode: '',
        city: '',
        iban: '',
        bic: '',
        bankName: '',
        taxNumber: '',
        letterheadPath: '',
        stampPath: '',
        signaturePath: '',
      });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('companies.createdSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Company creation error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('companies.creationFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        timer: 3000,
        showConfirmButton: false
      });
    },
  });

  // Update company mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      console.log('Updating company with id:', id, 'FormData');
      const response = await api.put(`/companies/${id}`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Company updated successfully:', response.data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      setShowForm(false);
      setEditingCompany(null);
      reset({
        name: '',
        email: '',
        address: '',
        postalCode: '',
        city: '',
        iban: '',
        bic: '',
        bankName: '',
        taxNumber: '',
        letterheadPath: '',
        stampPath: '',
        signaturePath: '',
      });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('companies.updatedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Company update error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('companies.updateFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        timer: 3000,
        showConfirmButton: false
      });
    },
  });

  // Delete company mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/companies/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('companies.deletedSuccess'),
        timer: 2000,
        showConfirmButton: false
      });
    },
    onError: (error) => {
      console.error('Company deletion error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('companies.deletionFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        timer: 3000,
        showConfirmButton: false
      });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: '',
      email: '',
      address: '',
      iban: '',
      bic: '',
      bankName: '',
      taxNumber: '',
      letterheadPath: '',
      stampPath: '',
      signaturePath: '',
    },
  });

  const onSubmit = (data) => {
    console.log('Form submitted with data:', data);
    console.log('Editing company:', editingCompany);
    
    // Create FormData for file uploads
    const formData = new FormData();
    
    // Add text fields
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('address', data.address);
    formData.append('postalCode', data.postalCode || '');
    formData.append('city', data.city || '');
    formData.append('iban', data.iban);
    formData.append('bic', data.bic);
    formData.append('bankName', data.bankName);
    if (data.taxNumber) {
      formData.append('taxNumber', data.taxNumber);
    }
    
    // Add file fields if they exist
    if (data.letterheadPath && data.letterheadPath[0]) {
      formData.append('letterheadPath', data.letterheadPath[0]);
    }
    if (data.stampPath && data.stampPath[0]) {
      formData.append('stampPath', data.stampPath[0]);
    }
    if (data.signaturePath && data.signaturePath[0]) {
      formData.append('signaturePath', data.signaturePath[0]);
    }
    
    console.log('FormData created with files');
    
    if (editingCompany) {
      console.log('Updating company:', editingCompany._id);
      updateMutation.mutate({ id: editingCompany._id, data: formData });
    } else {
      console.log('Creating new company');
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (company) => {
    setEditingCompany(company);
    reset(company);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCompany(null);
    reset({
      name: '',
      email: '',
      address: '',
      iban: '',
      bic: '',
      bankName: '',
      taxNumber: '',
      letterheadPath: '',
      stampPath: '',
      signaturePath: '',
    });
  };

  const handleDelete = (company) => {
    Swal.fire({
      title: t('companies.confirmDelete'),
      text: t('companies.confirmDeleteText', { name: company.name }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('companies.yesDelete')
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation.mutate(company._id);
      }
    });
  };

  const handleFilePreview = async (filePath, fileName, fileType) => {
    console.log('File preview requested:', { filePath, fileName, fileType });
    
    // Extract file extension from filePath, not from fileType
    const extension = filePath.split('.').pop().toLowerCase();
    const actualFileType = extension === 'pdf' ? 'pdf' : 'image';
    
    try {
      // Try to get base64 data first
      const base64Path = filePath.replace('/api/files/', '/api/files-base64/');
      const response = await fetch(base64Path);
      
      if (response.ok) {
        const data = await response.json();
        setPreviewFile({
          path: data.data, // Base64 data URL
          name: fileName,
          type: actualFileType
        });
      } else {
        // Fallback to original path
        setPreviewFile({
          path: filePath,
          name: fileName,
          type: actualFileType
        });
      }
    } catch (error) {
      console.log('Base64 fetch failed, using original path:', error);
      // Fallback to original path
      setPreviewFile({
        path: filePath,
        name: fileName,
        type: actualFileType
      });
    }
    
    setShowFilePreview(true);
  };

  const closeFilePreview = () => {
    setShowFilePreview(false);
    setPreviewFile(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('companies.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('companies.description')}
          </p>
        </div>
        <button
          onClick={() => {
            setEditingCompany(null);
            reset({
              name: '',
              email: '',
              address: '',
              iban: '',
              bic: '',
              bankName: '',
              taxNumber: '',
              letterheadPath: '',
              stampPath: '',
              signaturePath: '',
            });
            setShowForm(true);
          }}
          className="btn btn-primary"
        >
          {t('companies.createCompany')}
        </button>
      </div>

      {/* Modal Form */}
      {showForm && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={handleCancel}
        >
          <div
            className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingCompany ? t('companies.editCompany') : t('companies.createCompany')}
            </h2>
          <form onSubmit={handleSubmit((data) => {
            console.log('Form validation passed, submitting:', data);
            onSubmit(data);
          }, (errors) => {
            console.log('Form validation errors:', errors);
          })} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('companies.companyName')} *</label>
                <input
                  {...register('name')}
                  className={`input ${errors.name ? 'input-error' : ''}`}
                  placeholder={t('companies.companyName')}
                />
                {errors.name && (
                  <p className="error-message">{t(errors.name.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('companies.email')} *</label>
                <input
                  {...register('email')}
                  type="email"
                  className={`input ${errors.email ? 'input-error' : ''}`}
                  placeholder={t('companies.email')}
                />
                {errors.email && (
                  <p className="error-message">{t(errors.email.message)}</p>
                )}
              </div>
            </div>
            <div>
              <label className="label">{t('companies.address')} *</label>
              <textarea
                {...register('address')}
                className={`input ${errors.address ? 'input-error' : ''}`}
                rows={3}
                placeholder={t('companies.address')}
              />
              {errors.address && (
                <p className="error-message">{t(errors.address.message)}</p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('companies.postalCode')} *</label>
                <input
                  {...register('postalCode')}
                  type="text"
                  className={`input ${errors.postalCode ? 'input-error' : ''}`}
                  placeholder={t('companies.postalCode')}
                />
                {errors.postalCode && (
                  <p className="error-message">{t(errors.postalCode.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('companies.city')} *</label>
                <input
                  {...register('city')}
                  type="text"
                  className={`input ${errors.city ? 'input-error' : ''}`}
                  placeholder={t('companies.city')}
                />
                {errors.city && (
                  <p className="error-message">{t(errors.city.message)}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('companies.iban')} *</label>
                <input
                  {...register('iban')}
                  className={`input ${errors.iban ? 'input-error' : ''}`}
                  placeholder={t('companies.iban')}
                />
                {errors.iban && (
                  <p className="error-message">{t(errors.iban.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('companies.bic')} *</label>
                <input
                  {...register('bic')}
                  className={`input ${errors.bic ? 'input-error' : ''}`}
                  placeholder={t('companies.bic')}
                />
                {errors.bic && (
                  <p className="error-message">{t(errors.bic.message)}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('companies.bankName')} *</label>
                <input
                  {...register('bankName')}
                  className={`input ${errors.bankName ? 'input-error' : ''}`}
                  placeholder={t('companies.bankName')}
                />
                {errors.bankName && (
                  <p className="error-message">{t(errors.bankName.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('companies.taxNumber')}</label>
                <input
                  {...register('taxNumber')}
                  className="input"
                  placeholder={t('companies.taxNumber')}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="label">{t('companies.letterhead')}</label>
                <input
                  {...register('letterheadPath')}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">{t('companies.fileFormatNote')}</p>
                {editingCompany && editingCompany.letterheadPath && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <p className="text-xs text-gray-600 mb-1">{t('companies.currentFile')}:</p>
                    <button
                      onClick={() => handleFilePreview(
                        `/api/files/companies/${encodeURIComponent(editingCompany.letterheadPath)}`,
                        'Current Letterhead',
                        editingCompany.letterheadPath.split('.').pop().toLowerCase()
                      )}
                      className="text-blue-600 hover:text-blue-500 text-xs cursor-pointer"
                    >
📄 {t('companies.viewCurrentLetterhead')}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="label">{t('companies.stamp')}</label>
                <input
                  {...register('stampPath')}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">{t('companies.fileFormatNote')}</p>
                {editingCompany && editingCompany.stampPath && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <p className="text-xs text-gray-600 mb-1">{t('companies.currentFile')}:</p>
                    <button
                      onClick={() => handleFilePreview(
                        `/api/files/companies/${encodeURIComponent(editingCompany.stampPath)}`,
                        'Current Stamp',
                        editingCompany.stampPath.split('.').pop().toLowerCase()
                      )}
                      className="text-blue-600 hover:text-blue-500 text-xs cursor-pointer"
                    >
🏷️ {t('companies.viewCurrentStamp')}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label className="label">{t('companies.signature')}</label>
                <input
                  {...register('signaturePath')}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">{t('companies.fileFormatNote')}</p>
                {editingCompany && editingCompany.signaturePath && (
                  <div className="mt-2 p-2 bg-gray-50 rounded border">
                    <p className="text-xs text-gray-600 mb-1">{t('companies.currentFile')}:</p>
                    <button
                      onClick={() => handleFilePreview(
                        `/api/files/companies/${encodeURIComponent(editingCompany.signaturePath)}`,
                        'Current Signature',
                        editingCompany.signaturePath.split('.').pop().toLowerCase()
                      )}
                      className="text-blue-600 hover:text-blue-500 text-xs cursor-pointer"
                    >
✍️ {t('companies.viewCurrentSignature')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                className="btn btn-primary"
              >
                {createMutation.isPending || updateMutation.isPending
                  ? t('common.loading')
                  : t('common.save')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {showFilePreview && previewFile && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={closeFilePreview}
        >
          <div
            className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {previewFile.name} {t('companies.preview')}
              </h3>
              <button
                onClick={closeFilePreview}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="border rounded-lg overflow-hidden">
              {previewFile.type === 'pdf' ? (
                <div className="w-full h-[600px] bg-gray-100 flex flex-col items-center justify-center">
                  <object
                    data={`${previewFile.path}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0`}
                    type="application/pdf"
                    className="w-full h-full border-0"
                    title={previewFile.name}
                    style={{ width: '100%', height: '100%' }}
                  >
                    <div className="text-center p-8">
                      <p className="text-gray-600 mb-4">{t('companies.pdfCannotDisplay')}</p>
                      <a
                        href={previewFile.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-primary"
                      >
                        {t('companies.openInNewTab')}
                      </a>
                    </div>
                  </object>
                  <div className="mt-2 text-sm text-gray-600">
                    {t('companies.pdfViewerNote')}
                  </div>
                </div>
              ) : (
                <div className="w-full h-[600px] bg-gray-100 flex items-center justify-center">
                  <img
                    src={previewFile.path}
                    alt={previewFile.name}
                    className="max-w-full max-h-full object-contain"
                    onLoad={() => console.log('Image loaded successfully')}
                    onError={(e) => {
                      console.log('Image failed to load:', previewFile.path);
                      e.target.style.display = 'none';
                      // Show error message
                      const container = e.target.parentElement;
                      container.innerHTML = `
                        <div class="text-center p-8">
                          <p class="text-gray-600 mb-4">${t('companies.imageCannotDisplay')}</p>
                          <a href="${previewFile.path}" target="_blank" rel="noopener noreferrer" class="btn btn-primary">
                            ${t('companies.openImageNewTab')}
                          </a>
                        </div>
                      `;
                    }}
                  />
                </div>
              )}
            </div>
            
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {t('companies.file')}: {previewFile.name}
              </div>
              <div className="flex space-x-2">
                <a
                  href={previewFile.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary text-sm"
                >
                  {t('companies.openInNewTab')}
                </a>
                <a
                  href={previewFile.path}
                  download
                  className="btn btn-primary text-sm"
                >
                  {t('companies.download')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Companies List */}
      <div className="card">
        {companies && companies.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table min-w-full">
                  <thead>
                    <tr>
                      <th className="min-w-[150px]">{t('companies.companyName')}</th>
                      <th className="min-w-[200px]">{t('companies.email')}</th>
                      <th className="min-w-[150px]">{t('companies.address')}</th>
                      <th className="min-w-[100px]">{t('companies.employees')}</th>
                      <th className="min-w-[80px]">{t('companies.letterhead')}</th>
                      <th className="min-w-[80px]">{t('companies.stamp')}</th>
                      <th className="min-w-[80px]">{t('companies.signature')}</th>
                      <th className="min-w-[120px]">{t('common.actions')}</th>
                    </tr>
                  </thead>
              <tbody>
                {companies.map((company) => (
                  <tr key={company._id}>
                    <td className="font-medium">{company.name}</td>
                    <td className="text-sm">{company.email}</td>
                    <td className="max-w-[150px] truncate text-sm">{company.address}</td>
                    <td className="text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {company.employeeCount || 0} {t('companies.employees')}
                      </span>
                    </td>
                    <td className="text-center">
                      {company.letterheadPath ? (
                        <button
                          onClick={() => handleFilePreview(
                            `/api/files/companies/${encodeURIComponent(company.letterheadPath)}`,
                            'Letterhead',
                            company.letterheadPath.split('.').pop().toLowerCase()
                          )}
                          className="text-green-600 hover:text-green-500 text-sm cursor-pointer"
                          title={t('companies.viewLetterhead')}
                        >
                          📄
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      {company.stampPath ? (
                        <button
                          onClick={() => handleFilePreview(
                            `/api/files/companies/${encodeURIComponent(company.stampPath)}`,
                            'Stamp',
                            company.stampPath.split('.').pop().toLowerCase()
                          )}
                          className="text-green-600 hover:text-green-500 text-sm cursor-pointer"
                          title={t('companies.viewStamp')}
                        >
                          🏷️
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="text-center">
                      {company.signaturePath ? (
                        <button
                          onClick={() => handleFilePreview(
                            `/api/files/companies/${encodeURIComponent(company.signaturePath)}`,
                            'Signature',
                            company.signaturePath.split('.').pop().toLowerCase()
                          )}
                          className="text-green-600 hover:text-green-500 text-sm cursor-pointer"
                          title={t('companies.viewSignature')}
                        >
                          ✍️
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEdit(company)}
                          className="text-primary-600 hover:text-primary-500 text-xs px-2 py-1 rounded bg-primary-50 hover:bg-primary-100"
                          title={t('companies.editCompany')}
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(company)}
                          className="text-red-600 hover:text-red-500 text-xs px-2 py-1 rounded bg-red-50 hover:bg-red-100"
                          title={t('companies.deleteCompany')}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('companies.noCompanies')}</h3>
            <p className="text-gray-500 mb-4">{t('companies.createFirst')}</p>
            <button
              onClick={() => {
                setEditingCompany(null);
                reset({
                  name: '',
                  email: '',
                  address: '',
                  iban: '',
                  bic: '',
                  bankName: '',
                  taxNumber: '',
                  letterheadPath: '',
                  stampPath: '',
                  signaturePath: '',
                });
                setShowForm(true);
              }}
              className="btn btn-primary"
            >
              {t('companies.createCompany')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Companies;
